import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Checkbox, Stack, Link, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import logger from '../../utils/logger';
import { usePermissions } from '../../contexts/PermissionContext';
import { setAutoSession } from '../../utils/authUtils';
// shouldShowOnboarding not used here

const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
}));

const FieldContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
}));

const LabelRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#303030',
  lineHeight: '20px'
}));

const RemindMeLink = styled(Link)(() => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#191919',
  textDecoration: 'underline',
  cursor: 'pointer',
  lineHeight: '20px',
  '&:hover': {
    color: '#000000',
  },
}));

const StyledInput = styled('input')(({ theme, error }) => ({
  width: '100%',
  height: '48px',
  padding: '12px 44px 12px 16px',
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#191919',
  backgroundColor: '#FFFFFF',
  border: error ? '1px solid #C30052' : '1px solid #8C8C8C',
  borderRadius: '4px',
  outline: 'none',
  boxShadow: error ? 'inset 0 0 0 1px #C30052' : 'inset 0 0 0 1px #8C8C8C',
  transition: 'all 0.2s ease',
  '&::placeholder': {
    color: '#8C8C8C',
    fontSize: '16px'
  },
  '&:focus': {
    borderColor: '#191919',
    boxShadow: 'inset 0 0 0 1px #191919'
  },
  '&:hover': {
    borderColor: '#191919',
    boxShadow: 'inset 0 0 0 1px #191919'
  },
  [theme.breakpoints.down('sm')]: {
    height: '44px',
    fontSize: '15px',
    padding: '10px 42px 10px 14px'
  }
}));

const PasswordWrapper = styled(Box)(() => ({
  position: 'relative',
  width: '100%'
}));

const PasswordToggle = styled(IconButton)(() => ({
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  padding: 6,
  color: '#6b7280'
}));

const ErrorText = styled(Typography)(() => ({
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#C30052',
  lineHeight: '20px',
  marginTop: '4px'
}));

const SignInButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '48px',
  borderRadius: '4px',
  backgroundColor: '#82B440',
  color: '#FFFFFF',
  fontFamily: 'PolySans, "Helvetica Neue", Arial, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#6FA030',
    boxShadow: '0 2px 8px rgba(130, 180, 64, 0.3)'
  },
  '&:active': {
    backgroundColor: '#5D8A28'
  },
  '&:disabled': {
    backgroundColor: '#CCCCCC',
    color: '#8C8C8C'
  },
  [theme.breakpoints.down('sm')]: {
    height: '44px',
    fontSize: '15px'
  }
}));

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const nextParam = urlParams.get('next');
  const trialTokenParam = urlParams.get('trialToken') || urlParams.get('trialtoken') || urlParams.get('trial');
  const locationIdParam = urlParams.get('locationId') || urlParams.get('locationid');
  const sourceParam = urlParams.get('source') || urlParams.get('utm_source');
  // If login was reached via a `next` param containing the trial info (e.g. /login?next=/trial-landing?trialToken=...)
  let decodedNextParams = null;
  if (!trialTokenParam && nextParam) {
    try {
      const decodedNext = decodeURIComponent(nextParam || '');
      const nextQsIndex = decodedNext.indexOf('?');
      const qs = nextQsIndex >= 0 ? decodedNext.substring(nextQsIndex + 1) : '';
      decodedNextParams = new URLSearchParams(qs);
    } catch (e) {
      decodedNextParams = null;
    }
  }
  const resolvedTrialToken = trialTokenParam || (decodedNextParams && (decodedNextParams.get('trialToken') || decodedNextParams.get('trialtoken') || decodedNextParams.get('token')));
  const resolvedSource = sourceParam || (decodedNextParams && (decodedNextParams.get('source') || decodedNextParams.get('utm_source')));
  const { checkUserPermissions } = usePermissions();

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isFormValid = () => email.trim() !== '' && password.trim() !== '' && validateEmail(email);

  // No trial persistence on login — normal login flow only
  React.useEffect(() => { }, [resolvedTrialToken]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');

    if (!isFormValid()) return;

    setLoading(true);

    try {
      // Clear any stale trial markers to avoid showing someone else's trial state
      try { sessionStorage.removeItem('trial_invite'); } catch (_) { }
      try { localStorage.removeItem('trial_invite'); } catch (_) { }
      try { localStorage.removeItem('came_from_trial'); } catch (_) { }
      try { sessionStorage.removeItem('came_from_trial'); } catch (_) { }

      // Clear login_source if user is NOT coming from ads page in this session
      // The marker should only persist if set during this same navigation flow
      // Check if referrer contains 'ads' or if there's a fresh ads marker in sessionStorage
      try {
        const freshAdsMarker = sessionStorage.getItem('login_source_fresh');
        if (!freshAdsMarker) {
          // Not a fresh navigation from ads page, clear any stale marker
          localStorage.removeItem('login_source');
        }
        // Clear the fresh marker after checking
        sessionStorage.removeItem('login_source_fresh');
      } catch (_) { }

      // Clear any stale admin session data from previous admin access
      // This prevents clients from seeing admin-viewed profiles after login
      sessionStorage.removeItem('adminAccessData');
      sessionStorage.removeItem('adminViewingUserId');
      sessionStorage.removeItem('currentUserContext');
      sessionStorage.removeItem('visibeen_default_profile');
      sessionStorage.removeItem('googleAccessToken');
      sessionStorage.removeItem('adminView_googleAccessToken');
      localStorage.removeItem('latestAdminAccessTempKey');
      // Clear cached GMB profiles to ensure fresh data for the new user
      localStorage.removeItem('availableGMBProfiles');
      localStorage.removeItem('availableGMBProfilesOwner');

      // Clear all monthly plan caches to prevent cross-user data leakage
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('monthly_plan_') || key.startsWith('weekly_products_services_'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`[Login] Cleared ${keysToRemove.length} monthly plan cache entries`);
      } catch (e) {
        console.warn('[Login] Failed to clear monthly plan caches:', e);
      }

      const response = await api.post('/customer/auth/login', {
        email,
        password,
        account_type: 'manual'
      });

      // Handle different response structures from backend
      // REST.success returns { error: null, code: 200, data: userData, message: ... }
      let userData = response?.data || response;

      // If userData has nested user object (from some auth flows)
      if (userData.user) {
        userData = {
          ...userData.user,
          token: userData.token || userData.user.token,
          refresh_token: userData.refresh_token || userData.user.refresh_token,
        };
      }

      console.log('=== LOGIN RESPONSE DEBUG ===');
      console.log('Raw response:', response);
      console.log('Extracted userData:', userData);
      console.log('onboarding_completed value:', userData?.onboarding_completed);

      setAutoSession(userData);

      if (userData.token) {
        localStorage.setItem('authToken', userData.token);
      }
      localStorage.setItem('userData', JSON.stringify(userData));

      // No trial post-processing — normal login flow only

      // AutoTokenManager.startAutoRefresh(); // Removed as it is not exported

      try {
        await checkUserPermissions();
      } catch (permissionError) {
        logger.warn('Could not refresh permissions after login:', permissionError);
      }

      logger.log('dY\u000f User role_id:', userData.role_id);

      // Only role_id 9 (Admin) gets dashboard pages
      if (Number(userData.role_id) === 9) {
        const defaultPages = [
          { page_id: 1, page_name: 'Leads' },
          { page_id: 2, page_name: 'Meetings' },
          { page_id: 3, page_name: 'Clients' },
          { page_id: 4, page_name: 'Demo Account' },
          { page_id: 5, page_name: 'Reports' },
          { page_id: 6, page_name: 'Local Citation' },
          { page_id: 7, page_name: 'Whats app' },
          { page_id: 8, page_name: 'Blogs' },
          { page_id: 9, page_name: 'Social Media' },
          { page_id: 10, page_name: 'Refer & Earn' },
          { page_id: 11, page_name: 'My Account' },
          { page_id: 12, page_name: 'Employee' },
          { page_id: 13, page_name: 'Attendance' },
          { page_id: 14, page_name: 'Holidays' },
        ];
        localStorage.setItem('dashboardPages', JSON.stringify(defaultPages));
      }

      console.log('=== LOGIN REDIRECT DEBUG ===');
      console.log('Full User data:', JSON.stringify(userData, null, 2));
      console.log('Role ID:', userData.role_id);
      console.log('User ID:', userData.id);
      console.log('User user_id:', userData.user_id);
      console.log('Customer ID:', userData.customer_id);

      setTimeout(() => {
        // If there's an explicit next parameter (from meeting link), prefer it for non-admin users
        // Also: if a trial invite exists (server returned it, or persisted in storage), take user to trial landing
        try {
          // If server returned trialInvite or client stored trial_invite, go to trial landing
          try {
            const stored = sessionStorage.getItem('trial_invite') || localStorage.getItem('trial_invite');
            if (stored) {
              try {
                const inv = JSON.parse(stored);
                // Prefer dashboard so global TrialOfferSnackbar shows as a popup
                try {
                  const inviteStr = JSON.stringify(inv);
                  try { sessionStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                  try { localStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                  try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
                } catch (_) { }
                navigate('/dashboard');
                return;
              } catch (_) { }
            }
            else {
              // No trial present for this login — ensure client markers cleared
              try { sessionStorage.removeItem('trial_invite'); } catch (_) { }
              try { localStorage.removeItem('trial_invite'); } catch (_) { }
              try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
            }
          } catch (_) { }
        } catch (e) { }

        if (nextParam) {

          // Track login if user used a trial token/source
          try {
            const usedToken = resolvedTrialToken || (response?.data?.trialInvite?.token) || null;
            const usedSource = resolvedSource || (response?.data?.trialInvite?.source) || null;
            if (usedToken || usedSource) {
              (async () => {
                try {
                  await api.post('/v1/tracking/login', {
                    token: usedToken,
                    source: usedSource,
                    userId: userData?.id || null,
                    email: userData?.email || null
                  });
                } catch (e) { console.warn('Tracking login failed', e); }
              })();
            }
          } catch (_) { }
          try {
            const decodedNext = decodeURIComponent(nextParam);
            // Only role_id 9 (Admin) should go to admin dashboard
            if (userData.role_id !== 9) {
              // If the next param includes a `source` (from trial link), create a lead for this user
              try {
                (async () => {
                  try {
                    let source = null;
                    try {
                      const nowParams = new URLSearchParams(window.location.search || '');
                      source = nowParams.get('source') || nowParams.get('utm_source') || null;
                    } catch (_) { source = null; }
                    if (!source) {
                      try {
                        const m = decodedNext.match(/(?:\?|&)(?:source|utm_source)=([^&]+)/i);
                        if (m) source = decodeURIComponent(m[1]);
                      } catch (_) { source = null; }
                    }
                    if (source) {
                      const payload = {
                        user_id: userData.id || null,
                        lead_source: source,
                        first_name: userData.full_name || userData.first_name || '',
                        email: userData.email || '',
                        phone_number: userData.phone || userData.mobile || '',
                        comment: 'Auto-created lead from trial link',
                        type: 'trial'
                      };
                      try {
                        // Use new ensure-lead endpoint to avoid duplicates
                        const resp = await api.post('/admin/leads/ensure-lead', payload);
                        console.log('Ensure lead response', resp);
                      } catch (err) { console.warn('Auto ensure-lead failed', err); }
                    }
                  } catch (_) { }
                })();
              } catch (_) { }
              // If decodedNext is an absolute URL on same origin, navigate there, else use router navigate
              // If the next param contains a trial token, persist a short-lived invite flag
              try {
                let candidateUrl = null;
                if (decodedNext.startsWith('http')) {
                  candidateUrl = new URL(decodedNext);
                } else if (decodedNext.startsWith('/')) {
                  candidateUrl = new URL(window.location.origin + decodedNext);
                }

                if (candidateUrl) {
                  const t = candidateUrl.searchParams.get('trialToken') || candidateUrl.searchParams.get('trialtoken') || candidateUrl.searchParams.get('token');
                  const sig = candidateUrl.searchParams.get('sig') || candidateUrl.searchParams.get('signature');
                  const locationId = candidateUrl.searchParams.get('locationId') || candidateUrl.searchParams.get('location');
                  const source = candidateUrl.searchParams.get('source') || candidateUrl.searchParams.get('utm_source') || null;
                  if (t) {
                    try {
                      const invite = { token: t, sig: sig || null, locationId: locationId || null, source: source || null, ts: Date.now() };
                      const inviteStr = JSON.stringify(invite);
                      try { sessionStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                      try { localStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                      console.log('[Login] Stored trial_invite from next param', invite);
                      try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
                      try { window.dispatchEvent(new StorageEvent('storage', { key: 'trial_invite', newValue: inviteStr })); } catch (_) { }
                    } catch (_) { }
                  }
                } else {
                  // Fallback: try to extract from query string using regex
                  const m = decodedNext.match(/[?&](?:trialToken|trialtoken|token)=([^&]+)/i);
                  if (m && m[1]) {
                    const t = decodeURIComponent(m[1]);
                    try {
                      const invite = { token: t, sig: null, locationId: null, source: null, ts: Date.now() };
                      const inviteStr = JSON.stringify(invite);
                      try { sessionStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                      try { localStorage.setItem('trial_invite', inviteStr); } catch (_) { }
                      console.log('[Login] Stored trial_invite from next param (fallback regex)', invite);
                      try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
                      try { window.dispatchEvent(new StorageEvent('storage', { key: 'trial_invite', newValue: inviteStr })); } catch (_) { }
                    } catch (_) { }
                  }
                }
              } catch (_) { }

              if (decodedNext.startsWith('http')) {
                const urlObj = new URL(decodedNext);
                if (urlObj.origin === window.location.origin) {
                  navigate(urlObj.pathname + urlObj.search + urlObj.hash);
                } else {
                  // External link: open in new tab
                  window.location.href = decodedNext;
                }
              } else {
                navigate(decodedNext);
              }
              return;
            }
          } catch (e) {
            // ignore malformed next param and continue with default behavior
            console.warn('Malformed next param, ignoring:', nextParam);
          }
        }

        // Only role_id 9 (Admin) redirects to admin dashboard
        const roleId = Number(userData.role_id);
        if (roleId === 9) {
          // Admin goes to admin dashboard
          console.log('>>> REDIRECT: Admin dashboard (role_id 9)');
          navigate('/admin-dashboard');
        }
        // Check if user is genuinely new using backend's isNewUser flag
        else {
          // The backend's /login endpoint returns { user, isNewUser } 
          // isNewUser is true ONLY if the user was just created during this login
          // For existing users, isNewUser is always false (see auth/index.js line 46)
          const isNewUserFromBackend = response?.data?.isNewUser === true;

          // ✅ FIX: Only NEW users (created just now) should see pricing/onboarding
          // Existing users in DB should NEVER see pricing, regardless of onboarding_completed status
          // Legacy users may have onboarding_completed = null - they are still existing users
          const hasCompletedOnboarding = !isNewUserFromBackend; // If not new, they've "completed" onboarding (skip it)

          console.log('=== ONBOARDING CHECK ===');
          console.log('- User ID:', userData.id || userData.user_id);
          console.log('- onboarding_completed (from API):', userData.onboarding_completed);
          console.log('- isNewUser (from backend):', isNewUserFromBackend);
          console.log('- Has completed onboarding (calculated):', hasCompletedOnboarding);

          // New users who haven't completed onboarding go to Pricing first
          if (!hasCompletedOnboarding) {
            console.log('>>> REDIRECT: Pricing page (NEW USER)');
            // Set force_pricing flag so PricingGate can enforce it
            try { localStorage.setItem('force_pricing', 'true'); } catch (_) { }
            // Clear localStorage onboarding flag so GMBInsightsPage doesn't redirect away
            const userId = userData.id || userData.user_id || userData.customer_id;
            if (userId) {
              localStorage.removeItem(`visibeen.onboardingComplete:${userId}`);
              console.log('>>> Cleared localStorage onboarding flag for user:', userId);
            }
            navigate('/pricing');
          }
          // Existing users go to standard dashboard
          else {
            console.log('>>> REDIRECT: Dashboard (EXISTING USER)');
            navigate('/dashboard');
          }
        }
      }, 100);
    } catch (err) {
      logger.error('Login error:', err);
      setError(err?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <FieldContainer>
        <LabelRow>
          <FieldLabel>Username or Email</FieldLabel>
          <RemindMeLink onClick={() => navigate('/forgot-password')}>
            Remind me
          </RemindMeLink>
        </LabelRow>
        <StyledInput
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={email !== '' && !validateEmail(email)}
          aria-label="Username or Email"
        />
        {email !== '' && !validateEmail(email) && (
          <ErrorText>Username or Email is required</ErrorText>
        )}
      </FieldContainer>

      <FieldContainer>
        <LabelRow>
          <FieldLabel>Password</FieldLabel>
          <RemindMeLink onClick={() => navigate('/forgot-password')}>
            Forgot
          </RemindMeLink>
        </LabelRow>
        <PasswordWrapper>
          <StyledInput
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-label="Password"
          />
          <PasswordToggle
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onClick={() => setShowPassword(s => !s)}
            size="small"
          >
            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </PasswordToggle>
        </PasswordWrapper>
      </FieldContainer>

      {error && (
        <ErrorText style={{ textAlign: 'center' }}>
          {error}
        </ErrorText>
      )}

      <SignInButton onClick={handleLogin} disabled={!isFormValid() || loading}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} thickness={5} sx={{ color: '#ffffff' }} />
            <span>Signing in...</span>
          </Box>
        ) : (
          'Sign in'
        )}
      </SignInButton>
    </FormContainer>
  );
};

export default LoginForm;
